import { Button } from "@sellar-io/components";
import { FulfillmentTypeListResponseType } from "@sellar-io/api-v2/types";
import { useForm } from "@tanstack/react-form";
import { useId } from "react";
import { Alert } from "../../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../../hooks/usePriceFormat";
import {
  ContainerTypeDeliveryChargeRulesForm,
  ContainerTypeDeliveryChargeRulesFormType,
} from "../forms/ContainerTypeDeliveryChargeRulesForm";
import { useContainerTypeDeliveryChargeRulesUpdate } from "../../../../hooks/api/v2/containerTypeDeliveryChargeRules/useContainerTypeDeliveryChargeRulesUpdate";
import { useContainerTypeDeliveryChargeRulesDelete } from "../../../../hooks/api/v2/containerTypeDeliveryChargeRules/useContainerTypeDeliveryChargeRulesDelete";

type ContainerTypeDeliveryChargeRule =
  FulfillmentTypeListResponseType[number]["ContainerTypeDeliveryChargeRules"][number];

export const ContainerTypeDeliveryChargeRuleUpdateDialog = ({
  active,
  onDismiss,
  existingDeliveryChargeRule,
}: {
  active: boolean;
  onDismiss: () => void;
  existingDeliveryChargeRule: ContainerTypeDeliveryChargeRule;
}) => {
  const formId = useId();

  const containerDeliveryChargeDeleteMutation =
    useContainerTypeDeliveryChargeRulesDelete(existingDeliveryChargeRule.id);

  const containerDeliveryChargeUpdateMutation =
    useContainerTypeDeliveryChargeRulesUpdate(existingDeliveryChargeRule.id);

  const form = useForm({
    defaultValues: {
      value: formatPriceApiToInput(existingDeliveryChargeRule.value),
      containerType: existingDeliveryChargeRule.containerType,
      deliveryCharge: 0,
    } satisfies ContainerTypeDeliveryChargeRulesFormType,
    onSubmit: ({ value }) => {
      const apiValue = formatPriceInputToApi(value.value);

      if (apiValue === null) throw new Error("apiValue should be defined");

      containerDeliveryChargeUpdateMutation.mutate(
        {
          containerType: value.containerType,
          value: apiValue,
          deliveryCharge: 0,
        },
        {
          onSuccess: () => {
            onDismiss();
            form.reset();
          },
        },
      );
    },
  });

  const onDelete = () => {
    containerDeliveryChargeDeleteMutation.mutate();
    onDismiss();
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Update free delivery rule"}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        {containerDeliveryChargeUpdateMutation.isError ? (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(containerDeliveryChargeUpdateMutation.error)
                ?.response?.data.message ?? "Unknown error occurred."
            }
          />
        ) : null}
        <ContainerTypeDeliveryChargeRulesForm form={form} formId={formId} />
      </DialogContent>

      <DialogCTA>
        <Button
          color={"red"}
          onClick={() => {
            onDelete();
          }}
        >
          Delete
        </Button>
        <Button
          type="submit"
          form={formId}
          loading={containerDeliveryChargeUpdateMutation.isPending}
        >
          Update
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
